/**
 * Apple IAP — purchase the practitioner monthly subscription via StoreKit.
 *
 * Lazy-loads the Capacitor IAP plugin only when running natively on iOS.
 * On every other platform (web, Android, even iOS web preview), the
 * functions return a `{ ok: false, reason }` so callers can fall back to
 * Stripe gracefully.
 *
 * Plugin used: `cordova-plugin-purchase` v13+ (the de-facto Capacitor
 * IAP wrapper Capacitor's own docs recommend). It exposes a global
 * `CdvPurchase` namespace once the plugin is installed in the iOS app.
 *
 * To install in the native shell:
 *   yarn add cordova-plugin-purchase
 *   npx cap sync ios
 *
 * The plugin is intentionally *not* listed as a hard dependency of the
 * web bundle — we feature-detect it at runtime so web builds don't break
 * if the plugin isn't installed in `node_modules`.
 */
import { api } from "./api";

let _store = null;       // CdvPurchase.Store singleton
let _productInfo = null; // cached server-supplied product IDs + price
let _readyPromise = null;

async function _getServerConfig() {
  if (_productInfo) return _productInfo;
  const { data } = await api.get("/iap/apple/config");
  _productInfo = data;
  return data;
}

/**
 * Initialize the IAP store. Idempotent — second call returns the same
 * Promise so multiple components can race-await without double-registering.
 */
export function initAppleIAP() {
  if (_readyPromise) return _readyPromise;
  _readyPromise = (async () => {
    const cfg = await _getServerConfig();
    if (!cfg.configured) {
      throw new Error("Apple IAP is not configured on the server yet");
    }
    // Plugin is wrapped in window.CdvPurchase when the cordova plugin is
    // installed in the iOS app. We grab it lazily so non-iOS builds never
    // touch it.
    const CdvPurchase = window.CdvPurchase;
    if (!CdvPurchase) {
      throw new Error(
        "Apple IAP plugin not available — make sure cordova-plugin-purchase " +
        "is installed in the iOS app and you're running on a real device " +
        "or TestFlight build (not simulator without StoreKit config)."
      );
    }
    const store = CdvPurchase.store;
    _store = store;

    const productId = cfg.productIds.practitionerSubscriptionMonthly;
    store.register([{
      id: productId,
      type: CdvPurchase.ProductType.PAID_SUBSCRIPTION,
      platform: CdvPurchase.Platform.APPLE_APPSTORE,
    }]);

    // Receipt verification handler — every time the store reports a new
    // verified or unverified transaction, we forward the receipt to our
    // backend. The backend re-verifies with Apple and grants access.
    store.when().approved(async (transaction) => {
      try {
        const appReceipt = store.localReceipts?.[0]?.nativeData?.appStoreReceipt
          || transaction.nativePurchase?.appStoreReceipt;
        if (!appReceipt) {
          console.warn("[apple-iap] approved tx with no appStoreReceipt", transaction);
          transaction.finish();
          return;
        }
        await api.post("/iap/apple/verify", {
          receiptData: appReceipt,
          productId: transaction.products?.[0]?.id || productId,
        });
        transaction.finish();
      } catch (err) {
        console.error("[apple-iap] verify failed", err);
      }
    });

    await store.initialize([CdvPurchase.Platform.APPLE_APPSTORE]);
    await store.update();
    return { store, productId };
  })();
  return _readyPromise;
}

/**
 * Begin the StoreKit purchase flow for the practitioner subscription.
 *
 * Returns:
 *   { ok: true } — Apple presented its paywall; result will arrive via the
 *     `approved()` listener in `initAppleIAP()` (which posts to our backend).
 *   { ok: false, reason } — could not start. Caller should fall back to
 *     Stripe / show an error.
 */
export async function purchasePractitionerSubscription() {
  try {
    const { store, productId } = await initAppleIAP();
    const product = store.get(productId);
    if (!product) {
      return { ok: false, reason: "product-not-loaded" };
    }
    const offer = product.getOffer();
    if (!offer) {
      return { ok: false, reason: "no-purchase-offer-available" };
    }
    await store.order(offer);
    return { ok: true };
  } catch (err) {
    return { ok: false, reason: err?.message || "iap-failed" };
  }
}

/** Restore prior purchases — required by Apple for any IAP UI. */
export async function restorePurchases() {
  try {
    const { store } = await initAppleIAP();
    await store.restorePurchases();
    return { ok: true };
  } catch (err) {
    return { ok: false, reason: err?.message || "restore-failed" };
  }
}

/** Cached product display info (price string, etc.) once `initAppleIAP` succeeded. */
export function getDisplayPrice() {
  if (!_store) return null;
  const productId = _productInfo?.productIds?.practitionerSubscriptionMonthly;
  if (!productId) return null;
  const p = _store.get(productId);
  const offer = p?.getOffer?.();
  const pricing = offer?.pricingPhases?.[0];
  return pricing?.price || null;
}
